import { bootstrap, event, optIn, optOut, set } from "vue-gtag";
import store from "@/store/index.js";
import { isProduction, isStaging } from "@/utils/env";
import { sanitizeUrl } from "@/utils/posthog-sanitize";

// Google Analytics is installed with bootstrap: false (main.js), so gtag.js is
// only loaded once the user consents to it in the CMP (see consent.js).
let hasAnalyticsConsent = false;
let isBootstrapped = false;

export function applyAnalyticsConsent(granted) {
  hasAnalyticsConsent = granted;
  if (granted) {
    optIn();
    if (!isBootstrapped) {
      isBootstrapped = true;
      bootstrap();
      updateGaPageLocation();
    }
  } else if (isBootstrapped) {
    // Withdrawn: sets window["ga-disable-<id>"], gtag.js stops sending.
    optOut();
    deleteGaCookies();
  }
}

// GA's own hits (enhanced-measurement page_views on history changes) would
// report the full URL incl. share token, Bobbie hash and login_hint. A
// config-level page_location would override this for good, freezing every
// hit to the first page, so it's set here — after loading and on navigation.
export function updateGaPageLocation() {
  if (!isBootstrapped) return;
  set({ page_location: sanitizeUrl(window.location.href) });
}

// GA's cookies (_ga, _ga_<id>) outlive optOut(); remove them on withdrawal.
// gtag sets them on the registrable domain (.airteam.cloud) by default.
function deleteGaCookies() {
  const names = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  const domains = [
    "",
    "; domain=.airteam.cloud",
    `; domain=${window.location.hostname}`,
  ];
  names.forEach((name) =>
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }),
  );
}

// Shared links carry their access token as ?token=, Bobbie/Control Center pass
// tokens in the hash, and links from airteam.cloud add login_hint=<email>: no
// URL reaches GA with its query or hash. Events without their own page_location
// would otherwise reuse the config's one, fixed at boot — give each the current page.
const URL_PARAMS = ["page_location", "page_referrer", "page_path"];

function sanitizeParams(params = {}) {
  const result = { ...params };
  URL_PARAMS.forEach((key) => {
    if (typeof result[key] === "string") result[key] = sanitizeUrl(result[key]);
  });
  if (!result.page_location) {
    result.page_location = sanitizeUrl(window.location.href);
  }
  return result;
}

/**
 * Sends a gtag event only if the user consented to Google Analytics, is not
 * an employee, and the environment is production or staging.
 *
 * @param {object} [options]
 * @param {boolean} [options.includeEmployees] also send the event for
 *   employees. Off by default so internal use does not skew the usual product
 *   metrics — turn it on only for events whose population is largely internal,
 *   and keep a `customer_type` param so the two stay separable in GA4.
 */
export function trackEvent(
  eventName,
  params,
  { includeEmployees = false } = {},
) {
  if (!hasAnalyticsConsent) return;
  if (!isProduction && !isStaging) return;
  if (!includeEmployees && store.getters.activeUserIsEmployee) return;
  event(eventName, sanitizeParams(params));
}
